import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowLeft, Check, Users, Lock } from "lucide-react";
import { api } from "../contexts/AuthContext";
import AvatarDisplay from "./AvatarDisplay";

const ICONS = ["🏠", "🎉", "🎮", "🎬", "🎵", "🌙", "☕", "🔥", "⭐", "🌈", "🍕", "🎲"];

export default function FriendsRoomCreate({ onClose, onCreated }) {
  const [step, setStep] = useState(1);       // 1=name/icon, 2=pick friends, 3=done
  const [name, setName] = useState("");
  const [icon, setIcon] = useState("🏠");
  const [friends, setFriends] = useState([]); // available friends
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/friends").then(r => setFriends(r.data.friends || [])).catch(() => {});
  }, []);

  const toggleFriend = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleCreate = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/friends-rooms", {
        name: name.trim(), icon, friend_ids: [...selected]
      });
      setResult(data);
      setStep(3);
      onCreated?.(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create room");
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-[#0E0E17] border border-purple-500/30 rounded-2xl w-full max-w-md shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-purple-500/15">
          <div className="flex items-center gap-2">
            {step > 1 && step < 3 && (
              <button onClick={() => setStep(s => s - 1)} className="text-slate-400 hover:text-white mr-1">
                <ArrowLeft className="w-4 h-4" />
              </button>
            )}
            <Lock className="w-4 h-4 text-purple-400" />
            <h2 className="font-bold text-white font-[Outfit]">
              {step === 1 && "Friends Room"}
              {step === 2 && "Pick Friends"}
              {step === 3 && "Room Created!"}
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6">
          {/* ── Step 1: Name + Icon ── */}
          {step === 1 && (
            <div className="space-y-5">
              <p className="text-sm text-slate-400">
                Create a <span className="text-white font-semibold">private room</span> for the friends you choose.
                Only invited friends who accept can see and enter it.
              </p>

              {/* Icon picker */}
              <div>
                <label className="block text-xs text-slate-400 mb-2 uppercase tracking-wide">Room Icon</label>
                <div className="flex flex-wrap gap-2">
                  {ICONS.map(ic => (
                    <button key={ic} type="button" onClick={() => setIcon(ic)}
                      className={`w-10 h-10 rounded-xl text-xl flex items-center justify-center border transition-all ${
                        icon === ic ? "border-cyan-400 bg-cyan-500/15 scale-110" : "border-slate-700 hover:border-purple-500/50"
                      }`}>
                      {ic}
                    </button>
                  ))}
                </div>
              </div>

              {/* Room name */}
              <div>
                <label className="block text-xs text-slate-400 mb-2 uppercase tracking-wide">Room Name</label>
                <div className="flex items-center gap-2 bg-[#141322] border border-slate-700 rounded-xl px-4 py-3 focus-within:border-purple-500 transition-colors">
                  <span className="text-xl">{icon}</span>
                  <input
                    data-testid="fr-room-name-input"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Friday Night Banter"
                    maxLength={40}
                    className="flex-1 bg-transparent text-white text-sm placeholder-slate-600 focus:outline-none"
                  />
                </div>
              </div>

              <button
                data-testid="fr-step1-next"
                disabled={name.trim().length < 2}
                onClick={() => setStep(2)}
                className="w-full py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-40"
              >
                Pick Friends →
              </button>
            </div>
          )}

          {/* ── Step 2: Pick friends ── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-400">Select friends to invite</p>
                {selected.size > 0 && (
                  <span className="text-xs font-mono bg-purple-500/20 text-purple-300 px-2 py-1 rounded-full">
                    {selected.size} selected
                  </span>
                )}
              </div>

              {friends.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No friends yet.</p>
                  <p className="text-xs mt-1">Add friends in chatrooms first.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin">
                  {friends.map(f => {
                    const isSel = selected.has(f.user_id);
                    return (
                      <button
                        key={f.user_id}
                        data-testid={`fr-friend-toggle-${f.user_id}`}
                        onClick={() => toggleFriend(f.user_id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                          isSel ? "border-purple-500/50 bg-purple-500/10" : "border-slate-700 hover:border-purple-500/30"
                        }`}
                      >
                        <AvatarDisplay avatarId={f.avatar_id} size="sm" status={f.is_online ? "online" : "offline"} showStatus />
                        <span className="flex-1 text-sm font-medium text-white">{f.alias}</span>
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
                          isSel ? "border-purple-400 bg-purple-500" : "border-slate-600"
                        }`}>
                          {isSel && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}

              {error && <p className="text-red-400 text-sm">{error}</p>}

              <button
                data-testid="fr-send-invitations-btn"
                onClick={handleCreate}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-semibold rounded-xl hover:opacity-90 active:scale-95 transition-all disabled:opacity-40"
              >
                {loading
                  ? "Creating..."
                  : selected.size === 0
                  ? "Create Room (no invitations yet)"
                  : `Send ${selected.size} Invitation${selected.size > 1 ? "s" : ""}`}
              </button>
            </div>
          )}

          {/* ── Step 3: Confirmation ── */}
          {step === 3 && result && (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-violet-600/30 to-cyan-500/30 border border-purple-500/30 flex items-center justify-center text-3xl">
                {result.icon}
              </div>
              <div>
                <h3 className="text-xl font-black text-white font-[Outfit]">{result.name}</h3>
                <p className="text-sm text-slate-400 mt-1">
                  Friends room created.{" "}
                  {result.invite_count > 0
                    ? `${result.invite_count} invitation${result.invite_count > 1 ? "s" : ""} sent.`
                    : "You can invite friends later."}
                </p>
              </div>
              <div className="flex gap-3">
                <button onClick={onClose}
                  className="flex-1 py-2.5 border border-slate-700 text-slate-300 rounded-xl text-sm hover:border-slate-500">
                  Back to Lobby
                </button>
                <button
                  data-testid="fr-enter-room-btn"
                  onClick={() => { onClose(); window.location.href = `/friends-room/${result.room_id}`; }}
                  className="flex-1 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white font-semibold rounded-xl text-sm hover:opacity-90"
                >
                  Enter Room
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
